/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { Dispatch, SetStateAction, useMemo, useState } from 'react';

import { Box } from '@mui/material';
import {
  GridColDef,
  GridRowSelectionModel,
  GridSortModel,
} from '@mui/x-data-grid';
import { useQuery } from '@tanstack/react-query';
import { isArray } from 'lodash-es';
import { useDebouncedCallback } from 'use-debounce';

import { fetchGroups, fetchUsers } from '../../axiosClient/authApi';
import { fetchTags } from '../../axiosClient/dictionaryApi';
import DataGridTable from '../../components/dataGrid/DataGridTable';
import { GridCellExpand } from '../../components/dataGrid/GridCellExpand';
import NameAvatar from '../../components/dataGrid/NameAvatar';
import TableAsyncSelectInput from '../../components/dataGrid/TableAsyncSelectInput';
import TableChipList from '../../components/dataGrid/TableChipList';
import TableHeaderWithSearch from '../../components/dataGrid/TableHeaderWIthSearch';
import TableSearchInputComponent from '../../components/dataGrid/TableSearchInputComponent';
import { IObject } from '../../interfaces/IObject';

interface IProps {
  setSelectedRow: Dispatch<SetStateAction<GridRowSelectionModel>>;
  selectedRow: GridRowSelectionModel;
  data: any;
  isLoading: boolean;
  pageNumber: number;
  setPageNumberChange: Dispatch<SetStateAction<number>>;
  setFilters: Dispatch<SetStateAction<IObject>>;
  setSorting?: Dispatch<SetStateAction<GridSortModel>>;
  filters?: IObject;
}

const ContractTable = (props: IProps) => {
  const {
    setSelectedRow,
    pageNumber,
    setPageNumberChange,
    setFilters,
    setSorting,
    filters,
    isLoading,
    data,
  } = props;

  const [searchType, setSearchType] = useState<boolean>(false);
  const [searchTags, setSearchTags] = useState<boolean>(false);
  const [searchAssignee, setSearchAssignee] = useState<boolean>(false);
  const [searchGroups, setSearchGroups] = useState<boolean>(false);

  const { data: groupData } = useQuery({
    queryKey: ['groups'],
    queryFn: async () => {
      const response = await fetchGroups();
      return response.results;
    },
  });

  const { data: users } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      const response = await fetchUsers();
      return response.results;
    },
  });

  const { data: tags } = useQuery({
    queryKey: ['tags'],
    queryFn: async () => {
      const response = await fetchTags();
      return response.results;
    },
  });

  const handleFilterChange = useDebouncedCallback(
    (label: string, value: string | string[]) => {
      setFilters((prev: IObject) => ({ ...prev, [label]: value }));
    },
    1000
  );

  const handleGroupClick = React.useCallback(
    (groups: any) => {
      const names = groups.map((data: any) => data?.name);
      handleFilterChange('groups', names);
      setSearchGroups(true);
    },
    [handleFilterChange]
  );

  const columns = useMemo<GridColDef<any>[]>(
    () => [
      {
        field: 'contract_type',
        headerName: 'Document Type',
        minWidth: 200,
        flex: 1,
        sortable: !searchType,
        renderHeader: () => {
          return searchType ? (
            <TableSearchInputComponent
              key="contract_type"
              setIsSearch={() => {
                setFilters((prev) => ({ ...prev, name: null }));
                setSearchType(false);
              }}
              placeholder="Search Document"
              handleChange={(e: React.BaseSyntheticEvent<HTMLInputElement>) =>
                handleFilterChange('contract_type', e.target.value)
              }
            />
          ) : (
            <TableHeaderWithSearch
              title="Document Type"
              setIsSearch={setSearchType}
            />
          );
        },
        renderCell: (Params: any) => {
          return (
            <GridCellExpand
              value={Params?.row?.contract_type}
              width={Params.colDef.computedWidth}
              onClick={() => window.open(`/dictionary/${Params?.row?.id}`)}
              cellStyle={{
                cursor: 'pointer',
              }}
            />
          );
        },
      },
      {
        field: 'analysis_type',
        headerName: 'Analysis Type',
        minWidth: 200,
        flex: 1,
      },
      {
        field: 'tags',
        headerName: 'Tags',
        minWidth: 200,
        flex: 1,
        sortable: false,
        renderHeader: () => {
          return searchTags ? (
            <TableSearchInputComponent
              key="tags"
              setIsSearch={() => {
                setFilters((prev) => ({ ...prev, tags: null }));
                setSearchTags(false);
              }}
              placeholder="Search Tags"
              renderCustomInput={() => (
                <TableAsyncSelectInput
                  key={
                    isArray(filters?.assignees)
                      ? (filters?.assignees?.[0] as string)
                      : 'tags'
                  }
                  options={tags}
                  label="Select tags"
                  valueKey="id"
                  value={filters?.assignees ? filters.assignees : []}
                  onChange={(value: string[]) =>
                    handleFilterChange('tags', value)
                  }
                />
              )}
            />
          ) : (
            <TableHeaderWithSearch title="Tags" setIsSearch={setSearchTags} />
          );
        },
        renderCell: (params) => (
          <TableChipList
            id={`tags-${params?.row?.id}`}
            list={params.row.tags || []}
          />
        ),
      },
      {
        field: 'owner',
        headerName: 'Uploader',
        maxWidth: 300,
        width: 250,
        sortable: false,
        minWidth: 150,
        renderHeader: () => {
          return searchAssignee ? (
            <TableSearchInputComponent
              key="assignees"
              setIsSearch={() => {
                setFilters((prev) => ({ ...prev, created_by: null }));
                setSearchAssignee(false);
              }}
              placeholder="Search assignees"
              renderCustomInput={() => (
                <TableAsyncSelectInput
                  key={
                    isArray(filters?.assignees)
                      ? (filters?.assignees?.[0] as string)
                      : 'assignees'
                  }
                  options={users}
                  label="Select assignees"
                  valueKey="id"
                  value={filters?.assignees ? filters.assignees : []}
                  onChange={(value: string[]) =>
                    handleFilterChange('created_by', value)
                  }
                />
              )}
            />
          ) : (
            <TableHeaderWithSearch
              title="Created By"
              setIsSearch={setSearchAssignee}
            />
          );
        },
        renderCell: (params) => {
          return (
            <Box sx={{ cursor: 'pointer' }}>
              <NameAvatar
                firstName={params?.row?.created_by?.first_name}
                lastName={params?.row?.created_by?.last_name}
              />
            </Box>
          );
        },
      },
      {
        field: 'groups',
        headerName: 'Groups',
        maxWidth: 300,
        width: 250,
        sortable: false,
        minWidth: 150,
        renderCell: (params) => (
          <TableChipList
            id={`groups-${params?.row?.id}`}
            labelKey="name"
            list={params?.row?.groups || []}
            onClick={() => handleGroupClick(params?.row?.groups)}
          />
        ),
        renderHeader: () => {
          return searchGroups ? (
            <TableSearchInputComponent
              key="groups"
              setIsSearch={() => {
                setFilters((prev) => ({ ...prev, groups: null }));
                setSearchGroups(false);
              }}
              placeholder="Search Groups"
              renderCustomInput={() => (
                <TableAsyncSelectInput
                  key={
                    isArray(filters?.groups)
                      ? (filters?.groups?.[0] as string)
                      : 'groups'
                  }
                  options={groupData}
                  label="Select Project Groups"
                  valueKey="id"
                  value={filters?.groups ? filters.groups : []}
                  onChange={(value: string[]) =>
                    handleFilterChange('groups', value)
                  }
                />
              )}
            />
          ) : (
            <TableHeaderWithSearch
              title=" Groups"
              setIsSearch={setSearchGroups}
            />
          );
        },
      },
    ],
    [
      handleFilterChange,
      setFilters,
      searchType,
      searchTags,
      searchAssignee,
      searchGroups,
      users,
      groupData,
    ]
  );

  return (
    <Box>
      <DataGridTable
        rows={data?.results || []}
        columns={columns}
        rowCount={data?.count}
        isLoading={isLoading}
        setSelectedRow={setSelectedRow}
        pageNumber={pageNumber}
        setPageNumberChange={setPageNumberChange}
        setSorting={setSorting}
      />
    </Box>
  );
};

export default ContractTable;
